/**
 * Linked Item — tappable row navigating to the Item detail page.
 * Always visible. Shows "No linked item" if item_code is missing.
 */

import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { useGetDoc } from "@/lib/hooks/useFrappeQuery";
import { getChannelConfig } from "./shared";

export function MarketplaceLinkedItemWidget({ section, formData }: ExperienceWidgetProps) {
  const navigate = useNavigate();
  const config = getChannelConfig(section.description || "");
  const itemCode = formData?.[config.itemCode] as string | undefined;

  // Check if the linked Item still exists (handles deleted Item edge case)
  const { isError: itemNotFound, isLoading: checkingItem } = useGetDoc(
    "Item",
    itemCode ?? "",
    { retry: false, staleTime: 5 * 60 * 1000 },
  );

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Linked Item
      </h3>
      <div className="rounded-2xl overflow-hidden bg-card">
        {itemCode && checkingItem ? (
          /* Loading — show item code without chevron to avoid flash */
          <div className="flex items-center gap-4 px-4 py-5">
            <span className="text-lg text-foreground">{itemCode}</span>
          </div>
        ) : itemCode && !itemNotFound ? (
          /* Item exists — tappable row */
          <button
            type="button"
            className="flex items-center gap-4 px-4 py-5 w-full text-left hover:bg-muted/50 transition-colors"
            onClick={() => navigate(`/inventory/item/${encodeURIComponent(itemCode)}`)}
          >
            <span
              className="text-lg flex-1 min-w-0 truncate"
              style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
            >
              {itemCode}
            </span>
            <ChevronRight className="h-5 w-5 text-muted-foreground/60 shrink-0" />
          </button>
        ) : itemCode && itemNotFound ? (
          /* Item deleted — non-tappable fallback */
          <div className="flex items-center gap-4 px-4 py-5">
            <span className="text-lg text-muted-foreground">Item not found</span>
          </div>
        ) : (
          /* No item_code at all */
          <div className="flex items-center gap-4 px-4 py-5">
            <span className="text-lg text-muted-foreground">No linked item</span>
          </div>
        )}
      </div>
    </div>
  );
}
